import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

type Particle = {
  x: number
  y: number
  baseX: number
  baseY: number
  vx: number
  vy: number
  amp: number
  speed: number
  phase: number
  r: number
}

// Same coordinate space as the old SVG viewBox, so the layout is unchanged --
// only the rendering and interaction moved to canvas.
const VB_W = 100
const VB_H = 88
const DIM_DOTS = [
  { x: 22, y: 30, r: 5 }, { x: 58, y: 18, r: 4 }, { x: 84, y: 42, r: 5 },
  { x: 40, y: 58, r: 4 }, { x: 12, y: 70, r: 5 }, { x: 70, y: 74, r: 4 },
  { x: 92, y: 14, r: 3 }, { x: 6, y: 12, r: 3 },
]
const GOLD = { x: 52, y: 44, r: 7 }

// Ported to the same canvas + cursor-repel physics as the hero's
// ParticleSignal, at Leo's request ("more like the line hero thing") --
// same spring-back-to-target math, same velocity clamp, same touchstart
// poke for mobile. The dim dots idle-orbit a wander target forever
// (the "chaotic drift" Leo picked earlier) and give way to the cursor;
// the gold dot stays anchored and untouched, just pulsing its ring.
export function ScatterDiagram({
  className = '',
  reduceMotion = false,
}: {
  className?: string
  reduceMotion?: boolean
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const particlesRef = useRef<Particle[]>([])
  const mouseRef = useRef({ x: -9999, y: -9999 })

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let raf = 0
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const start = performance.now()

    function resize() {
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      if (rect.width <= 0 || rect.height <= 0) return
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      const sx = canvas.width / VB_W
      const sy = canvas.height / VB_H
      const s = Math.min(sx, sy)
      particlesRef.current = DIM_DOTS.map((d, i) => ({
        x: d.x * sx,
        y: d.y * sy,
        baseX: d.x * sx,
        baseY: d.y * sy,
        vx: 0,
        vy: 0,
        amp: (3 + (i % 3) * 1.5) * s,
        speed: 0.35 + (i % 4) * 0.12,
        phase: i * 1.3,
        r: d.r * s,
      }))
    }

    resize()
    // Setting canvas.width/height (inside resize) always clears the bitmap,
    // even to the same size. That's invisible while the RAF loop is
    // running, but for reduced-motion visitors nothing repaints
    // afterward -- a resize (address-bar collapse on scroll, orientation
    // change) would otherwise leave the canvas permanently blank.
    function onWindowResize() {
      resize()
      cancelAnimationFrame(raf)
      draw()
    }
    window.addEventListener('resize', onWindowResize)

    function onPointerMove(e: PointerEvent) {
      const rect = canvas!.getBoundingClientRect()
      mouseRef.current = { x: (e.clientX - rect.left) * dpr, y: (e.clientY - rect.top) * dpr }
    }
    function onPointerLeave() {
      mouseRef.current = { x: -9999, y: -9999 }
    }
    canvas.addEventListener('pointermove', onPointerMove)
    canvas.addEventListener('pointerleave', onPointerLeave)

    function onTouchStart(e: TouchEvent) {
      const touch = e.touches[0]
      if (!touch) return
      const rect = canvas!.getBoundingClientRect()
      const tx = (touch.clientX - rect.left) * dpr
      const ty = (touch.clientY - rect.top) * dpr
      const radius = canvas!.width * 0.3
      for (const p of particlesRef.current) {
        const dx = p.x - tx
        const dy = p.y - ty
        const dist = Math.hypot(dx, dy)
        if (dist < radius) {
          const force = (1 - dist / radius) * 8
          p.vx += (dx / (dist || 1)) * force
          p.vy += (dy / (dist || 1)) * force
        }
      }
    }
    canvas.addEventListener('touchstart', onTouchStart, { passive: true })

    const dim = getComputedStyle(document.documentElement).getPropertyValue('--n-200').trim() || '#241f14'
    const accent = getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#e0a458'
    const maxSpeed = 10

    function draw() {
      if (!ctx || !canvas) return
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      const t = (performance.now() - start) / 1000
      const mouse = mouseRef.current
      const repelRadius = canvas.width * 0.16

      for (const p of particlesRef.current) {
        if (!reduceMotion) {
          const targetX = p.baseX + Math.cos(t * p.speed + p.phase) * p.amp
          const targetY = p.baseY + Math.sin(t * p.speed * 1.3 + p.phase) * p.amp

          const dxm = p.x - mouse.x
          const dym = p.y - mouse.y
          const distm = Math.hypot(dxm, dym)
          if (distm < repelRadius) {
            const force = (1 - distm / repelRadius) * 5
            p.vx += (dxm / (distm || 1)) * force
            p.vy += (dym / (distm || 1)) * force
          }

          p.vx += (targetX - p.x) * 0.02
          p.vy += (targetY - p.y) * 0.02
          p.vx *= 0.86
          p.vy *= 0.86
          const speed = Math.hypot(p.vx, p.vy)
          if (speed > maxSpeed) {
            p.vx = (p.vx / speed) * maxSpeed
            p.vy = (p.vy / speed) * maxSpeed
          }
          p.x += p.vx
          p.y += p.vy
        } else {
          p.x = p.baseX
          p.y = p.baseY
        }

        ctx.fillStyle = dim
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2)
        ctx.fill()
      }

      const sx = canvas.width / VB_W
      const sy = canvas.height / VB_H
      const s = Math.min(sx, sy)
      const gx = GOLD.x * sx
      const gy = GOLD.y * sy
      const gr = GOLD.r * s

      ctx.lineWidth = Math.max(1, dpr)
      ctx.strokeStyle = accent
      if (!reduceMotion) {
        const cycle = 2.2
        const progress = (t % cycle) / cycle
        ctx.globalAlpha = 0.5 * (1 - progress)
        ctx.beginPath()
        ctx.arc(gx, gy, gr * (1.3 + progress * 1.6), 0, Math.PI * 2)
        ctx.stroke()
        ctx.globalAlpha = 1
      } else {
        ctx.globalAlpha = 0.35
        ctx.beginPath()
        ctx.arc(gx, gy, gr * 1.85, 0, Math.PI * 2)
        ctx.stroke()
        ctx.globalAlpha = 1
      }

      ctx.fillStyle = accent
      ctx.beginPath()
      ctx.arc(gx, gy, gr, 0, Math.PI * 2)
      ctx.fill()

      if (!reduceMotion) raf = requestAnimationFrame(draw)
    }

    draw()

    return () => {
      window.removeEventListener('resize', onWindowResize)
      canvas.removeEventListener('pointermove', onPointerMove)
      canvas.removeEventListener('pointerleave', onPointerLeave)
      canvas.removeEventListener('touchstart', onTouchStart)
      cancelAnimationFrame(raf)
    }
  }, [reduceMotion])

  return <canvas ref={canvasRef} aria-hidden="true" className={cn('block aspect-[100/88] h-auto w-full', className)} />
}
